import { useMemo } from "react";
import { FieldValues, useFormContext, Controller, Path, Validate } from "react-hook-form";
import { FormControl, FormControlLabel, FormHelperText, InputLabel, Radio, RadioGroup, SxProps } from "@mui/material";

interface SelectInputProps<T extends string | number> {
  source: Path<FieldValues>;
  label: string;
  options: Array<[T, string]>;
  validate?: Validate<T, FieldValues>;
  sx?: SxProps;
  disabled?: boolean;
  testId?: string;
  onChange?: (value: Partial<FieldValues>) => void;
}

let _radioGroupLabelIdCounter = 1;

export const RadioGroupInput = <T extends string | number>({
  source,
  label,
  options,
  validate,
  sx = {},
  disabled,
  testId,
  onChange,
}: SelectInputProps<T>) => {
  const labelId = useMemo(() => `RadioGroup-Label-${_radioGroupLabelIdCounter++}`, []);
  const { control, formState } = useFormContext<FieldValues>();
  const error = formState.errors[source]?.message as string | undefined;

  return (
    <FormControl disabled={disabled || formState.isSubmitting} sx={sx} data-testid={testId}>
      <InputLabel id={labelId} error={!!error} sx={{ m: 0 }}>
        {label}
      </InputLabel>
      <Controller
        name={source}
        control={control}
        rules={{ validate: validate as any }}
        render={({ field }) => (
          <RadioGroup
            row
            value={field.value ?? ""}
            onChange={(_, val) => {
              field.onChange(val);
              if (onChange) {
                onChange({ [source]: val });
              }
            }}
          >
            {options.map(([optValue, optLabel]) => (
              <FormControlLabel key={optValue} value={optValue} control={<Radio />} label={optLabel} />
            ))}
          </RadioGroup>
        )}
      />
      {error && <FormHelperText error>{error}</FormHelperText>}
    </FormControl>
  );
};
